/**
 * Prestige system logic
 * Headless - no GUI
 */

import type { GameState } from '../state.js';
import { getConquestGcBonus, getConquestOfflineBonus } from './conquest.js';

/** Calculate GC earned from prestige 1 */
export function calculateGcEarned(totalIpEarned: number, state: GameState): number {
  // GC = floor(10 * cbrt(totalIP / 1e12))
  let gc = Math.floor(10 * Math.cbrt(totalIpEarned / 1e12));

  // Minimum guarantee on first prestige
  if (gc < 5) gc = 5;

  // Prestige2 galaxySpeed: x2 per level
  const galaxySpeedLv = state.prestige2Upgrades.galaxySpeed;
  if (galaxySpeedLv > 0) gc = Math.floor(gc * Math.pow(2, galaxySpeedLv));

  // Achievement GC bonuses
  let gcPct = 0;
  const achGcBonuses: Record<string, number> = {
    P02: 10, P03: 10, P04: 15, P05: 20, P06: 25,
  };
  for (const [achId, pct] of Object.entries(achGcBonuses)) {
    if (state.achievements[achId]?.unlocked) gcPct += pct;
  }
  gc = Math.floor(gc * (1 + gcPct / 100));

  // Conquest facility GC bonus
  const conquestGcPct = getConquestGcBonus(state);
  if (conquestGcPct > 0) gc = Math.floor(gc * (1 + conquestGcPct / 100));

  return gc;
}

/** Check if prestige 1 is available */
export function canPrestige(state: GameState): boolean {
  return state.totalIpThisRun >= 10_000_000_000;
}

/** Calculate Cores earned from prestige 2 */
export function calculateCoresEarned(totalGcEarned: number, state: GameState): number {
  // Core = floor(3 * sqrt(totalGC / 100))
  let cores = Math.floor(3 * Math.sqrt(totalGcEarned / 100));
  if (cores < 1) cores = 1;

  // Achievement Core bonuses
  if (state.achievements['P07']?.unlocked) cores += 3;
  let corePct = 0;
  if (state.achievements['P08']?.unlocked) corePct += 20;
  cores = Math.floor(cores * (1 + corePct / 100));

  return cores;
}

/** Check if prestige 2 is available */
export function canPrestige2(state: GameState): boolean {
  return state.prestigeCount >= 15 && state.totalGcEarned >= 100;
}

/** Get prestige 1 starting IP (from infiltrationAccel) */
export function getStartingIp(state: GameState): number {
  // 1% of prestige threshold (100M) per level
  return state.prestigeUpgrades.infiltrationAccel * 100_000_000;
}

/** Get offline cap hours */
export function getOfflineCapHours(state: GameState): number {
  let base = 8;
  base += state.prestigeUpgrades.offlineExpand * 1;
  // Special upgrade: offlineCapHours
  for (const suId of state.specialUpgradesBought) {
    if (suId === 'timeWarp') base = 12;
  }
  // Achievement H04: +2 hours
  if (state.achievements['H04']?.unlocked) base += 2;
  return base;
}

/** Get offline efficiency (percentage of CPS earned offline) */
export function getOfflineEfficiency(state: GameState): number {
  let base = 50; // 50% base
  base += state.prestigeUpgrades.offlineEfficiency * 10;
  // Special upgrade: shadowNetwork +25%
  if (state.specialUpgradesBought.includes('shadowNetwork')) base += 25;
  // Conquest facility offline bonus
  base += getConquestOfflineBonus(state);
  // Prestige2: spacetimeWarp = 100%
  if (state.prestige2Upgrades.spacetimeWarp > 0) base = 100;
  return Math.min(100, base);
}
